import type { RadioOption } from '../../../../types/common';
import type { Option } from '@/components/ui/multi-select';
import type {
  AddPropertiesFormData,
  PropertyRequest
} from '../../../../types/property.types';
import type { PropertyDetailFormValues } from '../../property-detail/types/property-detail.types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export enum dropDownURLS {
  statesURL = 'states',
  propertyTypeURL = 'propertytype',
  propertyStatusURL = 'propertystatus',
  leaseStatusURL = 'leasestatus'
}

export const radioOption: RadioOption[] = [
  { label: 'Yes', id: 'rent-ready-yes', value: 'Yes' },
  { label: 'No', id: 'rent-ready-no', value: 'No' },
  { label: 'Not Set', id: 'rent-ready-notset', value: 'Notset' }
];

export const isActiveRadioOption: RadioOption[] = [
  { label: 'Yes', id: 'is-active-yes', value: 'Yes' },
  { label: 'No', id: 'is-active-no', value: 'No' }
];

export const DEFAULT_LAT = 39.76258282667817;
export const DEFAULT_LNG = -104.98241027688901;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MapValue {
  zipCode: string;
  state: string;
  city: string;
  address: string;
  lat: number;
  lng: number;
}

export interface MapFormDataResult {
  mapValue: MapValue;
  country: string;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export const rentReadySelection = (
  rentReadyValue: string | boolean | null | undefined
): string => {
  if (rentReadyValue === 'Not set') return 'Notset';
  return rentReadyValue === 'Yes' || rentReadyValue === true ? 'Yes' : 'No';
};

export const isListedSelection = (
  isListedValue: boolean | null | undefined
): string => {
  if (isListedValue === null || isListedValue === undefined) return 'Notset';
  return isListedValue === true ? 'Yes' : 'No';
};

export const isActiveSelection = (
  isActiveValue: boolean | null | undefined
): string => {
  return isActiveValue === true ? 'Yes' : 'No';
};

export const setOptionsData = (
  selected: string | null | undefined,
  options: Option[],
  setData: (opt: Option | undefined) => void
) => {
  if (selected != null) {
    const selectedOption = options.filter(
      (obj: Option) => obj.value === selected
    );
    setData(selectedOption[0]);
  }
};

export const getAddressComponent = (
  type: string,
  places: google.maps.places.PlaceResult
) =>
  places.address_components?.find((value) =>
    value.types.some((item) => item === type)
  );

export const extractMapFormData = (
  places: google.maps.places.PlaceResult
): MapFormDataResult => {
  let mapAddressValue: string;
  const mapPostalCode = getAddressComponent('postal_code', places);
  const city =
    getAddressComponent('locality', places) ||
    getAddressComponent('sublocality_level_1', places);
  const state = getAddressComponent('administrative_area_level_1', places);
  const countryValue = getAddressComponent('country', places);
  const streetAddressValue = getAddressComponent('street_number', places);
  const routeAddressValue = getAddressComponent('route', places);
  const premisesAddressValue = getAddressComponent('premise', places);

  if (streetAddressValue || routeAddressValue) {
    if (!streetAddressValue?.long_name) {
      mapAddressValue = `${routeAddressValue?.long_name}`;
    } else if (!routeAddressValue?.long_name) {
      mapAddressValue = `${streetAddressValue?.long_name}`;
    } else {
      mapAddressValue = `${streetAddressValue.long_name} ${routeAddressValue.long_name}`;
    }
  } else {
    mapAddressValue = premisesAddressValue?.long_name ?? '';
  }

  const loc = places.geometry?.location;
  return {
    mapValue: {
      address: mapAddressValue,
      zipCode: mapPostalCode ? mapPostalCode.long_name : '',
      city: city ? city.long_name : '',
      state: state ? state.short_name : '',
      lat: loc?.lat() ?? DEFAULT_LAT,
      lng: loc?.lng() ?? DEFAULT_LNG
    },
    country: countryValue ? countryValue.short_name : ''
  };
};

/**
 * Hydrate Formik initial values from order-flow property data.
 * Only used when `isFromOrder=true`; the old edit-route branch has been
 * removed (property-detail page handles all property editing now).
 */
export const buildOrderFlowFormValues = (
  data: Record<string, unknown>
) => ({
  propId: undefined,
  values: {
    address: (data.address as string) ?? '',
    zipCode: (data.zipCode as string) ?? '',
    city: (data.city as string) ?? '',
    bedrooms: data.bedrooms ? Number(data.bedrooms) : 0,
    bathrooms: data.bathrooms ? Number(data.bathrooms) : 0,
    squareFoot: (data.squareFoot as string) ?? '',
    // Fall back to legacy propertyID/unitId so users with persisted session
    // state from before the field rename keep their in-flight order draft.
    clientPropertyId:
      (data.clientPropertyId as string) ?? (data.propertyID as string) ?? '',
    portfolioName: (data.portfolioName as string) ?? '',
    propertyGroup: (data.propertyGroup as string) ?? '',
    state: (data.state as string) ?? '',
    propertyStatus: (data.propertyStatus as string) ?? '',
    PropertyType: (data.PropertyType as string) ?? '',
    entity: (data.entity as string) ?? '',
    entityUUID: (data.entityUUID as string) ?? '',
    other: data.unitNumber as string,
    clientUnitId: (data.clientUnitId as string) ?? (data.unitId as string) ?? '',
    rentReady: rentReadySelection(data.rentReady as string | boolean | null),
    availableDate: (data.availableDate as string) ?? '',
    leaseStartDate: (data.leaseStartDate as string) ?? '',
    leaseEndDate: (data.leaseEndDate as string) ?? '',
    noticeGivenDate: (data.noticeGivenDate as string) ?? '',
    moveInDate: (data.moveInDate as string) ?? '',
    moveOutDate: (data.moveOutDate as string) ?? '',
    leaseSignedDate: (data.leaseSignedDate as string) ?? '',
    listingDate: (data.listingDate as string) ?? '',
    isListed: isListedSelection(data.isListed as boolean | null),
    isActive: isActiveSelection(data.isActive as boolean | null),
    isOccupied: (data.isOccupied as string) ?? 'vacant',
    leaseStatus: (data.leaseStatus as string) ?? '',
    lat: data.lat ? Number(data.lat) : DEFAULT_LAT,
    lng: data.lng ? Number(data.lng) : DEFAULT_LNG
  },
  propertyTypeKey: data.PropertyType as string
});

/**
 * Build the PropertyRequest body for the POST /properties (create) endpoint.
 * Forces `isActive: true`; tri-state fields (rentReady, isListed) are not
 * sent — the create endpoint sets them server-side.
 */
export const buildCreatePropertyRequest = (
  formValues: AddPropertiesFormData,
): PropertyRequest => ({
  propertyType: formValues.PropertyType ?? '',
  address1: formValues.address,
  unitNumber: formValues.other === '' ? null : formValues.other,
  state: formValues.state,
  city: formValues.city,
  zipCode: formValues.zipCode,
  bedrooms: formValues.bedrooms,
  bathrooms: formValues.bathrooms,
  squareFeet: formValues.squareFoot ? Number(formValues.squareFoot) : null,
  clientPropertyId: formValues.clientPropertyId,
  propertyStatus: formValues.propertyStatus ?? '',
  entity: formValues.entity ?? '',
  entityUUID: formValues.entityUUID || undefined,
  portfolioName: formValues.portfolioName,
  propertyGroupName: formValues.propertyGroup,
  clientUnitId: formValues.clientUnitId,
  availableDate: formValues.availableDate,
  leaseStartDate: formValues.leaseStartDate,
  leaseEndDate: formValues.leaseEndDate,
  noticeGivenDate: formValues.noticeGivenDate,
  moveInDate: formValues.moveInDate,
  moveOutDate: formValues.moveOutDate,
  leaseSignedDate: formValues.leaseSignedDate,
  latitude: formValues.latitude,
  longitude: formValues.longitude,
  listingDate: formValues.listingDate,
  isOccupied: formValues.isOccupied === 'occupied',
  leaseStatus: formValues.leaseStatus === '' ? null : formValues.leaseStatus,
  country: formValues.country,
  isActive: true,
});

/**
 * Build the PropertyRequest body for the PUT /properties/:id (update) endpoint.
 * Handles tri-state Yes/No/Notset radios for rentReady / isListed / isActive.
 * Does NOT include `isOccupied` — occupancy is derived from propertyStatus on
 * the property-detail page, not edited directly via this form.
 */
export const buildUpdatePropertyRequest = (
  formValues: PropertyDetailFormValues,
): PropertyRequest => {
  const body: PropertyRequest = {
    propertyType: formValues.PropertyType ?? '',
    address1: formValues.address,
    unitNumber: formValues.other === '' ? '' : formValues.other,
    state: formValues.state,
    city: formValues.city,
    zipCode: formValues.zipCode,
    bedrooms: formValues.bedrooms,
    bathrooms: formValues.bathrooms,
    squareFeet: formValues.squareFoot ? Number(formValues.squareFoot) : null,
    clientPropertyId: formValues.clientPropertyId,
    propertyStatus: formValues.propertyStatus ?? '',
    entity: formValues.entity ?? '',
    entityUUID: formValues.entityUUID || undefined,
    portfolioName: formValues.portfolioName,
    propertyGroupName: formValues.propertyGroup,
    clientUnitId: formValues.clientUnitId,
    availableDate: formValues.availableDate,
    leaseStartDate: formValues.leaseStartDate,
    leaseEndDate: formValues.leaseEndDate,
    noticeGivenDate: formValues.noticeGivenDate,
    moveInDate: formValues.moveInDate,
    moveOutDate: formValues.moveOutDate,
    leaseSignedDate: formValues.leaseSignedDate,
    latitude: formValues.latitude,
    longitude: formValues.longitude,
    listingDate: formValues.listingDate,
    leaseStatus: formValues.leaseStatus === '' ? null : formValues.leaseStatus,
    country: formValues.country,
  };

  body.rentReady =
    formValues.rentReady === 'Yes'
      ? true
      : formValues.rentReady === 'No'
      ? false
      : null;
  body.isListed =
    formValues.isListed === 'Yes'
      ? true
      : formValues.isListed === 'No'
      ? false
      : null;
  if (formValues.isActive === 'Yes') body.isActive = true;
  else if (formValues.isActive === 'No') body.isActive = false;

  return body;
};

export const getSubmitErrorMessage = (
  code: string
): { type: 'error' | 'default'; message: string; navigate?: boolean } | null => {
  if (code.includes('A property with the same data already exists')) {
    return { type: 'error', message: 'A property with the same data already exists' };
  }
  if (code.includes(' Duplicate property exists for address')) {
    return { type: 'error', message: 'A property with this address already exists' };
  }
  if (code.includes('Property already exists and data has been updated')) {
    return { type: 'default', message: 'Property already exists and data has been updated', navigate: true };
  }
  if (code.includes('Property already exists') || code.includes('edited')) {
    return { type: 'error', message: 'Property already exists' };
  }
  return null;
};
